import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/cn";

/** 1, …, 4, 5, 6, …, 20 — with `null` marking an ellipsis gap. */
export function pageWindow(
  currentPage: number,
  totalPages: number,
  siblings = 1,
): (number | null)[] {
  if (totalPages <= 1) return [1];

  const pages: (number | null)[] = [];
  const start = Math.max(1, currentPage - siblings);
  const end = Math.min(totalPages, currentPage + siblings);

  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push(null);
  }
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push(null);
    pages.push(totalPages);
  }
  return pages;
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  disabled = false,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = pageWindow(currentPage, totalPages);
  const go = (page: number) => {
    if (disabled) return;
    const next = Math.min(totalPages, Math.max(1, page));
    if (next !== currentPage) onPageChange(next);
  };

  const firstRow =
    totalCount && pageSize ? (currentPage - 1) * pageSize + 1 : null;
  const lastRow =
    totalCount && pageSize
      ? Math.min(totalCount, currentPage * pageSize)
      : null;

  const buttonBase =
    "inline-grid h-9 min-w-9 place-items-center rounded-lg border px-2.5 text-sm font-medium " +
    "transition-colors duration-150 disabled:pointer-events-none disabled:opacity-40 " +
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500";

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        "flex flex-col-reverse items-center justify-between gap-3 pt-4 sm:flex-row",
        className,
      )}
    >
      {firstRow !== null && lastRow !== null ? (
        <p className="text-xs text-content-subtle tabular">
          Showing <span className="font-medium text-content-muted">{firstRow}</span>–
          <span className="font-medium text-content-muted">{lastRow}</span> of{" "}
          <span className="font-medium text-content-muted">{totalCount}</span>
        </p>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => go(currentPage - 1)}
          disabled={disabled || currentPage === 1}
          aria-label="Previous page"
          className={cn(buttonBase, "border-line-strong bg-surface hover:border-gold-400")}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>

        {pages.map((page, index) =>
          page === null ? (
            <span
              key={`gap-${index}`}
              aria-hidden="true"
              className="px-1 text-sm text-content-subtle"
            >
              …
            </span>
          ) : (
            <button
              key={page}
              type="button"
              onClick={() => go(page)}
              disabled={disabled}
              aria-current={page === currentPage ? "page" : undefined}
              className={cn(
                buttonBase,
                "tabular",
                page === currentPage
                  ? "border-gold-500 bg-gold-sheen text-ink-950 shadow-ir-sm"
                  : "border-line-strong bg-surface text-content-muted hover:border-gold-400 hover:text-content",
              )}
            >
              {page}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => go(currentPage + 1)}
          disabled={disabled || currentPage === totalPages}
          aria-label="Next page"
          className={cn(buttonBase, "border-line-strong bg-surface hover:border-gold-400")}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}
